/* eslint-disable array-callback-return */
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import "./style.css";
import { v4 as uuidV4 } from "uuid";
import * as XLSX from "xlsx";
import React, { useRef, useState } from "react";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import UndoIcon from "@mui/icons-material/Undo";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import moment from "moment";
import { message } from "antd";
import Alert from "@mui/material/Alert";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { colors } from "../brand";
import { useSelector } from "react-redux";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

import { MutatingDots } from "react-loader-spinner";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import ErrorIcon from "@mui/icons-material/Error";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import { Modal } from "antd";
import { Helmet } from "react-helmet-async";
import API from "../Api/api";
import toast from "react-hot-toast";
import secureLocalStorage from "react-secure-storage";
import { uniqueObjects } from "../utils/uniqueObjects";
import { getOutstandingSummary } from "../Api/index";

const getTransactionTypeStyles = (source = "") => {
  const normalized = String(source).toLowerCase();

  if (normalized.includes("debit")) {
    return {
      background: "linear-gradient(90deg, #fff1f0 0%, #fff7f6 100%)",
      borderLeft: "4px solid #ff7875",
    };
  }

  if (normalized.includes("credit")) {
    return {
      background: "linear-gradient(90deg, #f6ffed 0%, #fcfff8 100%)",
      borderLeft: "4px solid #73d13d",
    };
  }

  return {
    background: "#fafafa",
    borderLeft: "4px solid #d9d9d9",
  };
};

// Shown in the statement cells until the account type is chosen. It used to
// be a loading shimmer, which looked like the page was still working.
const Skeleton = () => <span style={{ color: "#cbd5e1" }}>—</span>;

// One line of the getting-started checklist
const StepLine = ({ done, n, children }) => (
  <Stack direction="row" spacing={1.2} alignItems="flex-start">
    {done ? (
      <CheckCircleIcon sx={{ color: colors.secondary, fontSize: 22 }} />
    ) : (
      <RadioButtonUncheckedIcon sx={{ color: "#cbd5e1", fontSize: 22 }} />
    )}
    <Typography sx={{ fontSize: 14.5, color: done ? colors.textSecondary : colors.textPrimary }}>
      <strong>{n}.</strong> {children}
    </Typography>
  </Stack>
);

const getAmountGroupBadgeStyle = () => ({
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: "999px",
  fontSize: "12px",
  fontWeight: 700,
  background: "linear-gradient(90deg, #e6f4ff 0%, #f0f5ff 100%)",
  color: "#1d39c4",
  border: "1px solid #91caff",
});

export default function Reconciliation() {
  const [open, setOpen] = useState(false);
  const [removedItemsOpen, setRemovedItemsOpen] = useState(false);
  const [autoRemovedItems, setAutoRemovedItems] = useState([]);
  const [selectedAutoRemovedRows, setSelectedAutoRemovedRows] = useState([]);

  const showModal = () => {
    setOpen(true);
  };

  const handleCancel = () => {
    setOpen(false);
  };
  const sortedAutoRemovedItems = React.useMemo(() => {
    return [...autoRemovedItems].sort((a, b) => {
      const amountDiff = Number(a.amount || 0) - Number(b.amount || 0);
      if (amountDiff !== 0) return amountDiff;
      return String(a.source || "").localeCompare(String(b.source || ""));
    });
  }, [autoRemovedItems]);

  const toggleAutoRemovedSelection = (id) => {
    setSelectedAutoRemovedRows((prev) =>
      prev.includes(id)
        ? prev.filter((itemId) => itemId !== id)
        : [...prev, id],
    );
  };

  const restoreSelectedAutoRemovedItems = () => {
    if (selectedAutoRemovedRows.length === 0) {
      message.info("Please select transaction(s) to post back");
      return;
    }

    const selectedItems = autoRemovedItems.filter((item) =>
      selectedAutoRemovedRows.includes(item.id),
    );

    if (selectedItems.length === 0) {
      message.info("No selected transactions found");
      return;
    }

    const restoredBranchCredit = [];
    const restoredBranchDebit = [];
    const restoredHeadOfficeCredit = [];
    const restoredHeadOfficeDebit = [];

    selectedItems.forEach((item) => {
      const baseRow = {
        id: uuidV4(),
        value_date: item.source_date || "",
        description: item.source_description || "",
        ids: Number(item.amount || 0),
      };

      if (item.source === "Branch Credit") {
        restoredBranchCredit.push({
          ...baseRow,
          credit_amt: Number(item.amount || 0),
        });
      }

      if (item.source === "Branch Debit") {
        restoredBranchDebit.push({
          ...baseRow,
          debit_amt: Number(item.amount || 0),
        });
      }

      if (item.source === "Head Office Credit") {
        restoredHeadOfficeCredit.push({
          ...baseRow,
          credit_amt: Number(item.amount || 0),
        });
      }

      if (item.source === "Head Office Debit") {
        restoredHeadOfficeDebit.push({
          ...baseRow,
          debit_amt: Number(item.amount || 0),
        });
      }
    });

    if (restoredBranchCredit.length > 0) {
      setBranchOutCr((prev) => [...prev, ...restoredBranchCredit]);
    }

    if (restoredBranchDebit.length > 0) {
      setBranchOutDr((prev) => [...prev, ...restoredBranchDebit]);
    }

    if (restoredHeadOfficeCredit.length > 0) {
      setHeadOfficeOutCr((prev) => [...prev, ...restoredHeadOfficeCredit]);
    }

    if (restoredHeadOfficeDebit.length > 0) {
      setHeadOfficeOutDr((prev) => [...prev, ...restoredHeadOfficeDebit]);
    }

    setAutoRemovedItems((prev) =>
      prev.filter((item) => !selectedAutoRemovedRows.includes(item.id)),
    );
    setSelectedAutoRemovedRows([]);
    message.success("Selected transaction(s) posted back to outstanding table");
  };

  const closeRemovedItemsModal = () => {
    setRemovedItemsOpen(false);
    setSelectedAutoRemovedRows([]);
  };

  const branchFileInputRef = useRef(null);
  const primaryFileInputRef = useRef(null);
  const user = JSON.parse(secureLocalStorage.getItem("profile"));
  const [headOfficeData, setHeadOfficeData] = React.useState([]);
  const [branchData, setBranchData] = React.useState([]);
  // branch outstanding debit and credit
  const [branchOutDr, setBranchOutDr] = React.useState([]);
  const [branchOutCr, setBranchOutCr] = React.useState([]);
  // head office outstanding debit and credit

  const [headOfficehOutDr, setHeadOfficeOutDr] = React.useState([]);
  const [headOfficehOutCr, setHeadOfficeOutCr] = React.useState([]);

  const [trigger, setTrigger] = React.useState(false);
  const [primary, setPrimary] = React.useState("head office");
  const [secondary, setSecondary] = React.useState("");
  const [branchOutCrPrev1, setbranchOutCrPrev] = React.useState([]);
  const [headOfficeOutCrPrev1, setheadOfficeOutCrPrev] = React.useState([]);
  const [branchOutDrPrev1, setbranchOutDrPrev] = React.useState([]);
  const [headOfficeOutDrPrev1, setheadOfficeOutDrPrev] = React.useState([]);
  const [loader, setLoader] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  const handleChangePrimary = (event) => {
    setPrimary(event.target.value);
  };
  const handleChangeSecondary = (event) => {
    setSecondary(event.target.value);
  };
  const [data, setData] = React.useState({
    date: "",
    branch: "",
  });

  const locations = useSelector((state) => state.location);

  function formatData(value_date, description, debit_amt, credit_amt) {
    return {
      value_date,
      description,
      debit_amt,
      credit_amt,
    };
  }

  // Cleaning the uploaded statements is expensive on large files, so it only
  // re-runs when a new file is uploaded, not on every keystroke or checkbox.
  const {
    headOfficeFinalData,
    branchFinalData,
    ho_dr_final,
    ho_cr_final,
    b_dr_final,
    b_cr_final,
  } = React.useMemo(() => {
    const headOfficeFinalData = headOfficeData?.map((i) => {
      return formatData(
        i.value_date,
        i.description,
        Math.abs(Number(i.debit_amt)),
        Math.abs(Number(i.credit_amt)),
      );
    });
    const branchFinalData = branchData?.map((i) => {
      return formatData(
        i.value_date,
        i.description,
        Math.abs(Number(i.debit_amt)),
        Math.abs(Number(i.credit_amt)),
      );
    });

    function debitData(value_date, description, debit_amt) {
      return {
        value_date,
        description,
        debit_amt,
      };
    }
    function creditData(value_date, description, credit_amt) {
      return {
        value_date,
        description,
        credit_amt,
      };
    }
    // ! Extracting Head office and Branch  Debit Data
    //  head office debit data
    const ho_dr = headOfficeFinalData?.map((debit) => {
      return debitData(debit.value_date, debit.description, debit.debit_amt);
    });

    const b_dr = branchFinalData?.map((debit) => {
      return debitData(debit.value_date, debit.description, debit.debit_amt);
    });

    // ! Extracting Head office and Branch Credit data
    const ho_cr = headOfficeFinalData?.map((credit) => {
      return creditData(credit.value_date, credit.description, credit.credit_amt);
    });
    const b_cr = branchFinalData?.map((credit) => {
      return creditData(credit.value_date, credit.description, credit.credit_amt);
    });

    let ho_dr_final_1 = [];
    let ho_cr_final_1 = [];
    // Final
    let b_dr_final_1 = [];
    let b_cr_final_1 = [];
    // ! we filtering out all zeros and that is credit
    // head office
    ho_dr.filter((d) => {
      if (d.debit_amt > 0) {
        return ho_dr_final_1.push(d);
      }
    });
    ho_cr.filter((d) => {
      if (d.credit_amt > 0) {
        return ho_cr_final_1.push(d);
      }
    });
    // branch
    b_dr.filter((d) => {
      if (d.debit_amt > 0) {
        return b_dr_final_1.push(d);
      }
    });
    b_cr.filter((d) => {
      if (d.credit_amt > 0) {
        return b_cr_final_1.push(d);
      }
    });
    const wordsToRemove = [
      "InterBranch Debit INTERCO ENTRY",
      "Inter-Branch Debit INTERCO ENTRY",
      "InterBranch Credit INTERCO ENTRY",
      "Inter-Branch Credit INTERCO ENTRY",
    ];

    const ho_dr_final = ho_dr_final_1.filter((outstanding) => {
      for (const word of wordsToRemove) {
        if (outstanding.description.includes(word)) {
          return false; // Exclude transaction if it contains any of the words to remove
        }
      }
      return true; // Include transaction if it doesn't contain any of the words to remove
    });
    const ho_cr_final = ho_cr_final_1.filter((outstanding) => {
      for (const word of wordsToRemove) {
        if (outstanding.description.includes(word)) {
          return false; // Exclude transaction if it contains any of the words to remove
        }
      }
      return true; // Include transaction if it doesn't contain any of the words to remove
    });
    // Final
    const b_dr_final = b_dr_final_1.filter((outstanding) => {
      for (const word of wordsToRemove) {
        if (outstanding.description.includes(word)) {
          return false; // Exclude transaction if it contains any of the words to remove
        }
      }
      return true; // Include transaction if it doesn't contain any of the words to remove
    });
    const b_cr_final = b_cr_final_1.filter((outstanding) => {
      for (const word of wordsToRemove) {
        if (outstanding.description.includes(word)) {
          return false; // Exclude transaction if it contains any of the words to remove
        }
      }
      return true; // Include transaction if it doesn't contain any of the words to remove
    });

    // we are assigning an id to each element so that we can use that to generate the difference
    b_cr_final.forEach((el) => Object.assign(el, { ids: el.credit_amt }));
    ho_cr_final.forEach((el) => Object.assign(el, { ids: el.credit_amt }));
    b_dr_final.forEach((el) => Object.assign(el, { ids: el.debit_amt }));
    ho_dr_final.forEach((el) => Object.assign(el, { ids: el.debit_amt }));

    return {
      headOfficeFinalData,
      branchFinalData,
      ho_dr_final,
      ho_cr_final,
      b_dr_final,
      b_cr_final,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headOfficeData, branchData]);

  // ho_dr_final is the final cleaned data for head office debit
  // ho_cr_final is the final cleaned data for head office credit

  // b_dr_final is the final cleaned data for branch debit
  //  b_cr_final is the final cleaned data for branch credit
  function getMatchedItems(
    array1,
    array2,
    amountKey,
    sourceLabel,
    matchedWithLabel,
  ) {
    const matched = [];

    // Unused indexes of array2 per amount, in order, so each lookup is direct
    // instead of scanning array2 for every row of array1
    const unusedIndexesByAmount = new Map();
    array2.forEach((item2, index) => {
      const amount = Number(item2.ids);
      if (!unusedIndexesByAmount.has(amount)) {
        unusedIndexesByAmount.set(amount, []);
      }
      unusedIndexesByAmount.get(amount).push(index);
    });

    array1.forEach((item1) => {
      const candidates = unusedIndexesByAmount.get(Number(item1.ids));
      const matchIndex = candidates?.length ? candidates.shift() : -1;

      if (matchIndex !== -1) {

        matched.push({
          id: uuidV4(),
          value_date: item1.value_date || array2[matchIndex]?.value_date || "",
          description:
            item1.description || array2[matchIndex]?.description || "",
          amount: Number(item1[amountKey] || item1.ids || 0),
          source: sourceLabel,
          matched_with: matchedWithLabel,
          source_description: item1.description || "",
          matched_description: array2[matchIndex]?.description || "",
          source_date: item1.value_date || "",
          matched_date: array2[matchIndex]?.value_date || "",
        });
      }
    });

    return matched;
  }

  function getDifference(array1, array2) {
    const difference = [];
    const idCounts = {};

    // Count the occurrences of each id in array2
    for (const obj2 of array2) {
      const ids = obj2.ids;
      idCounts[ids] = (idCounts[ids] || 0) + 1;
    }

    // Find objects unique to array1 and decrement count in idCounts for each match in array2
    for (const obj1 of array1) {
      const ids = obj1.ids;
      if (idCounts[ids] && idCounts[ids] > 0) {
        idCounts[ids]--;
      } else {
        difference.push(obj1);
      }
    }

    // Find objects unique to array2 (with remaining count > 0 in idCounts)
    const difference2 = [];
    for (const obj2 of array2) {
      const ids = obj2.ids;
      if (idCounts[ids] && idCounts[ids] > 0) {
        difference2.push(obj2);
        idCounts[ids]--;
      }
    }

    return difference2; // Return the difference from array2
  }
  const fetchPrevious = async () => {
    try {
      // Ask the server for this branch's open item count, not every item
      const { data: summary } = await getOutstandingSummary();
      const outstandingsAll = (summary.results?.branches || [])
        .filter(
          (row) =>
            row.branch === `${secondary} head office` ||
            row.branch === `head office ${secondary}`,
        )
        .reduce((total, row) => total + row.count, 0);
      if (outstandingsAll * 1 <= 0) {
        toast(
          (t) => (
            <span>
              This branch does not have previous outstandings{" "}
              <button onClick={() => toast.dismiss(t.id)}>Close</button>
            </span>
          ),
          { duration: 6000 },
        );
        return;
      }
      setLoader(true);
      const dates = [];
      const prevBranchOutCr = await API.get(
        `/api/v1/outstanding-credit/previous-reconciliation/${
          secondary + " " + primary
        }`,
      );
      const prevHeadOutCr = await API.get(
        `/api/v1/outstanding-credit/previous-reconciliation/${
          primary + " " + secondary
        }`,
      );
      const prevBranchOutDr = await API.get(
        `/api/v1/outstanding-debit/previous-reconciliation/${
          secondary + " " + primary
        }`,
      );
      const prevHeadOutDr = await API.get(
        `/api/v1/outstanding-debit/previous-reconciliation/${
          primary + " " + secondary
        }`,
      );

      if (prevBranchOutCr.data[0]) {
        dates.push(
          new Date(prevBranchOutCr.data[0].latestDate).toLocaleDateString(),
        );
      }
      if (prevHeadOutCr.data[0]) {
        dates.push(
          new Date(prevHeadOutCr.data[0].latestDate).toLocaleDateString(),
        );
      }
      if (prevBranchOutDr.data[0]) {
        dates.push(
          new Date(prevBranchOutDr.data[0].latestDate).toLocaleDateString(),
        );
      }
      if (prevHeadOutDr.data[0]) {
        dates.push(
          new Date(prevHeadOutDr.data[0].latestDate).toLocaleDateString(),
        );
      }
      const newDate = dates ? dates.map((d) => new Date(d)) : "";
      const maxDate = dates ? new Date(Math.max(...newDate)) : "";
      const finalDate = dates[0] ? moment(maxDate).format("YYYY-MM-DD") : "";

      if (finalDate) {
        setLoader(true);
        const branchOutCrPrev = await API.get(
          `/api/v1/outstanding-credit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${finalDate}`,
        );
        const headOfficeOutCrPrev = await API.get(
          `/api/v1/outstanding-credit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${finalDate}`,
        );
        const branchOutDrPrev = await API.get(
          `/api/v1/outstanding-debit?location=secondary&branch=${
            secondary + " " + primary
          }&submission_date=${finalDate}`,
        );
        const headOfficeOutDrPrev = await API.get(
          `/api/v1/outstanding-debit?location=primary&branch=${
            primary + " " + secondary
          }&submission_date=${finalDate}`,
        );
        setbranchOutCrPrev(branchOutCrPrev.data);
        setheadOfficeOutCrPrev(headOfficeOutCrPrev.data);
        setbranchOutDrPrev(branchOutDrPrev.data);
        setheadOfficeOutDrPrev(headOfficeOutDrPrev.data);
        // -----------------------------------------
        setLoader(false);
      } else if (!finalDate) {
        setbranchOutCrPrev([]);
        setheadOfficeOutCrPrev([]);
        setbranchOutDrPrev([]);
        setheadOfficeOutDrPrev([]);
        setLoader(false);
      }
      setLoader(false);
    } catch (error) {}
  };
  React.useEffect(() => {
    if (secondary && primary && data.date) {
      fetchPrevious();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primary, secondary, data.date]);
  // Ids are only given once: a new id on every render changes the row keys and
  // makes React rebuild every row of every table, which froze big reconciliations
  headOfficeOutCrPrev1?.forEach((el) =>
    Object.assign(el, { ids: el.credit_amt, id: el.id || uuidV4() }),
  );
  headOfficeOutDrPrev1?.forEach((el) =>
    Object.assign(el, { ids: el.debit_amt, id: el.id || uuidV4() }),
  );
  branchOutCrPrev1?.forEach((el) =>
    Object.assign(el, { ids: el.credit_amt, id: el.id || uuidV4() }),
  );
  branchOutDrPrev1?.forEach((el) =>
    Object.assign(el, { ids: el.debit_amt, id: el.id || uuidV4() }),
  );
  const withPrev = React.useMemo(
    () => ({
      ho_cr: [...ho_cr_final, ...headOfficeOutCrPrev1],
      ho_dr: [...ho_dr_final, ...headOfficeOutDrPrev1],
      b_dr: [...b_dr_final, ...branchOutDrPrev1],
      b_cr: [...b_cr_final, ...branchOutCrPrev1],
    }),
    [
      ho_cr_final,
      ho_dr_final,
      b_dr_final,
      b_cr_final,
      headOfficeOutCrPrev1,
      headOfficeOutDrPrev1,
      branchOutDrPrev1,
      branchOutCrPrev1,
    ],
  );
  let ho_cr_final_with_prev = withPrev.ho_cr;
  let ho_dr_final_with_prev = withPrev.ho_dr;
  let b_dr_final_with_prev = withPrev.b_dr;
  let b_cr_final_with_prev = withPrev.b_cr;

  // Rows of each list grouped by amount, built once per list, so the tables
  // look up matches instead of scanning the whole list for every row
  const rowsByAmountCache = React.useRef(new WeakMap());
  function rowsWithAmount(arr, amountKey, amount) {
    let byAmount = rowsByAmountCache.current.get(arr);
    if (!byAmount) {
      byAmount = {};
      rowsByAmountCache.current.set(arr, byAmount);
    }
    if (!byAmount[amountKey]) {
      const groups = new Map();
      arr.forEach((item) => {
        const key = item[amountKey];
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(item);
      });
      byAmount[amountKey] = groups;
    }
    return byAmount[amountKey].get(amount) || [];
  }

  function filterOutOutstanding(arr, outstanding) {
    let remaining = [
      ...rowsWithAmount(arr, "credit_amt", outstanding.credit_amt),
    ];

    // remove ONLY one occurrence that matches outstanding fields
    const indexToRemove = remaining.findIndex(
      (item) =>
        item.credit_amt === outstanding.credit_amt &&
        item.description === outstanding.description &&
        item.value_date === outstanding.value_date,
    );

    if (indexToRemove !== -1) {
      remaining.splice(indexToRemove, 1);
    }

    return remaining;
  }
  function filterOutOutstandingDebit(arr, outstanding) {
    let remaining = [
      ...rowsWithAmount(arr, "debit_amt", outstanding.debit_amt),
    ];

    // remove ONLY one occurrence that matches outstanding fields
    const indexToRemove = remaining.findIndex(
      (item) =>
        item.debit_amt === outstanding.debit_amt &&
        item.description === outstanding.description &&
        item.value_date === outstanding.value_date,
    );

    if (indexToRemove !== -1) {
      remaining.splice(indexToRemove, 1);
    }

    return remaining;
  }

  //! I will come back later
  const reconcile = async (e) => {
    e.preventDefault();
    if (headOfficeFinalData[0] && branchFinalData[0]) {
      if (secondary && primary && data.date) {
        // -----------------we will first fetch previous reconciliation-------------------------
        if (branchOutCr.length > 0 && branchOutDr.length > 0) {
          const confirm = window.confirm("Do you want to reconcile again?");
          if (!confirm) {
            message.info("operation aborted");
            return;
          }
        }
        // -----------------------------------------

        const branchDr = getDifference(
          ho_cr_final_with_prev,
          b_dr_final_with_prev,
        );
        const branchCr = getDifference(
          ho_dr_final_with_prev,
          b_cr_final_with_prev,
        );
        const headOfficeDr = getDifference(
          b_cr_final_with_prev,
          ho_dr_final_with_prev,
        );
        const headOfficeCr = getDifference(
          b_dr_final_with_prev,
          ho_cr_final_with_prev,
        );

        const removedFromHoCreditVsBranchDebit = getMatchedItems(
          ho_cr_final_with_prev,
          b_dr_final_with_prev,
          "credit_amt",
          "Head Office Credit",
          "Branch Debit",
        );

        const removedFromHoDebitVsBranchCredit = getMatchedItems(
          ho_dr_final_with_prev,
          b_cr_final_with_prev,
          "debit_amt",
          "Head Office Debit",
          "Branch Credit",
        );

        const removedFromBranchCreditVsHoDebit = getMatchedItems(
          b_cr_final_with_prev,
          ho_dr_final_with_prev,
          "credit_amt",
          "Branch Credit",
          "Head Office Debit",
        );

        const removedFromBranchDebitVsHoCredit = getMatchedItems(
          b_dr_final_with_prev,
          ho_cr_final_with_prev,
          "debit_amt",
          "Branch Debit",
          "Head Office Credit",
        );

        const combinedRemoved = [
          ...removedFromHoCreditVsBranchDebit,
          ...removedFromHoDebitVsBranchCredit,
          ...removedFromBranchCreditVsHoDebit,
          ...removedFromBranchDebitVsHoCredit,
        ];

        const uniqueRemoved = uniqueObjects(combinedRemoved);

        setAutoRemovedItems(uniqueRemoved);
        setSelectedAutoRemovedRows([]);

        // branch
        setBranchOutDr(branchDr);
        setBranchOutCr(branchCr);
        // head office
        setHeadOfficeOutCr(headOfficeCr);
        setHeadOfficeOutDr(headOfficeDr);
        setPages({});
        setTrigger(true);
      } else {
        toast.error(
          "Please choose primary location,secondary,secondary location and date to reconcile",
        );
      }
    } else {
      toast.error("Please select branch and head office data");
      setTrigger(false);
    }
  };
  branchOutCr?.forEach((el) => {
    if (!el.id) el.id = uuidV4();
  });
  headOfficehOutCr?.forEach((el) => {
    if (!el.id) el.id = uuidV4();
  });
  branchOutDr?.forEach((el) => {
    if (!el.id) el.id = uuidV4();
  });
  headOfficehOutDr?.forEach((el) => {
    if (!el.id) el.id = uuidV4();
  });

  const ourOutStandingsDebits = [];
  const theirOutStandingsDebits = [];
  const ourOutStandingCredits = [];
  const theirOutStandingCredits = [];

  branchOutCr?.map((el) => theirOutStandingCredits.push(el.credit_amt));
  branchOutDr?.map((el) => theirOutStandingsDebits.push(el.debit_amt));
  headOfficehOutCr?.map((el) => ourOutStandingCredits.push(el.credit_amt));
  headOfficehOutDr?.map((el) => ourOutStandingsDebits.push(el.debit_amt));
  const ourOutStandingsDebitsTotal = ourOutStandingsDebits.reduce(
    (acc, val) => acc + val,
    0,
  );
  const theirOutStandingsDebitsTotal = theirOutStandingsDebits.reduce(
    (acc, val) => acc + val,
    0,
  );
  const ourOutStandingCreditsTotal = ourOutStandingCredits.reduce(
    (acc, val) => acc + val,
    0,
  );
  const theirOutStandingCreditsTotal = theirOutStandingCredits.reduce(
    (acc, val) => acc + val,
    0,
  );
  const [balancePerTheirBook, setBalancePerTheirBook] = React.useState("");
  const [balancePerMyBook, setBalancePerMyBook] = React.useState("");
  const [checked, setChecked] = React.useState(false);
  const [checked2, setChecked2] = React.useState(false);
  let balancePerOurBook = 0;

  if (checked) {
    balancePerOurBook =
      balancePerTheirBook * 1 +
      ourOutStandingCreditsTotal * 1 +
      theirOutStandingCreditsTotal -
      (ourOutStandingsDebitsTotal * 1 + theirOutStandingsDebitsTotal * 1);
  } else if (checked2) {
    balancePerOurBook =
      balancePerTheirBook * 1 -
      ourOutStandingCreditsTotal * 1 -
      theirOutStandingCreditsTotal +
      (ourOutStandingsDebitsTotal * 1 + theirOutStandingsDebitsTotal * 1);
  }

  const currencyFormatter = new Intl.NumberFormat("en-US", {
    style: "decimal",
    // maximumFractionDigits: 2,
  });
  function ageing(date1, date2) {
    const formatDate1 = moment(date1).format("YYYY-MM-DD");
    const formatDate2 = moment(date2).format("YYYY-MM-DD");
    const diff = moment(formatDate2).diff(moment(formatDate1), "days");
    return diff;
  }

  const handleCheckedChange = (e) => {
    setChecked2(false);
    setChecked(e.target.checked);
  };
  const handleCheckedChange2 = (e) => {
    setChecked(false);
    setChecked2(e.target.checked);
  };

  let selected = false;
  if (checked || checked2) {
    selected = true;
  } else {
    selected = false;
  }
  let type = "";
  if (checked) {
    type = "debit account";
  } else if (checked2) {
    type = "credit account";
  }

  const preparedBranchOutDr = branchOutDr?.map((el) =>
    Object.assign(el, {
      submission_date: data.date,
      branch: secondary + " " + primary,
      location: "secondary",
      account_type: type,
      prepared_by: "",
    }),
  );
  const preparedHeadOfficeOutDr = headOfficehOutDr?.map((el) =>
    Object.assign(el, {
      submission_date: data.date,
      branch: primary + " " + secondary,
      location: "primary",
      account_type: type,
      prepared_by: "",
    }),
  );
  const preparedBranchOutCr = branchOutCr?.map((el) =>
    Object.assign(el, {
      submission_date: data.date,
      branch: secondary + " " + primary,
      location: "secondary",
      account_type: type,
      prepared_by: "",
    }),
  );
  const preparedHeadOfficeOutCr = headOfficehOutCr?.map((el) =>
    Object.assign(el, {
      submission_date: data.date,
      branch: primary + " " + secondary,
      location: "primary",
      account_type: type,
      prepared_by: "",
    }),
  );
  const submissionData = {
    submission_date: data.date,
    branch: primary + " " + secondary,
    second_branch: secondary + " " + primary,
    account_type: type,
    secondary_balance: balancePerTheirBook,
    primary_balance: balancePerOurBook,
    approved: "false",
    status: "new prepared reconciliation",
    prepared_by: user.results.firstName + " " + user.results.lastName,
  };
  const sendingCreditData = [
    ...preparedBranchOutCr,
    ...preparedHeadOfficeOutCr,
  ];
  const sendingDebitData = [...preparedBranchOutDr, ...preparedHeadOfficeOutDr];
  const sendingCreditDataModified = sendingCreditData?.map(
    ({ _id, __v, balance, ...rest }) => rest,
  );
  const sendingDebitDataModified = sendingDebitData?.map(
    ({ _id, __v, balance, ...rest }) => rest,
  );

  // Multiple row selection and removing of selected rows here
  const [headOfficeOutCreditRows, setHeadOfficeOutCreditRows] = React.useState(
    [],
  );
  const [headOfficeOutDebitRows, setHeadOfficeOutDebitRows] = React.useState(
    [],
  );
  // Multiple row selection and removing of selected rows here
  const [branchOutCreditRows, setBranchOutCreditRows] = React.useState([]);
  const [branchOutDebitRows, setBranchOutDebitRows] = React.useState([]);

  const getSelectedTotal = (rows = [], selectedIndexes = [], amountKey) => {
    return selectedIndexes.reduce((sum, index) => {
      const amount = Number(rows?.[index]?.[amountKey] || 0);
      return sum + amount;
    }, 0);
  };

  const checkedSummary = React.useMemo(() => {
    const totalCheckedCredit =
      getSelectedTotal(
        preparedHeadOfficeOutCr,
        headOfficeOutCreditRows,
        "credit_amt",
      ) +
      getSelectedTotal(preparedBranchOutCr, branchOutCreditRows, "credit_amt");

    const totalCheckedDebit =
      getSelectedTotal(
        preparedHeadOfficeOutDr,
        headOfficeOutDebitRows,
        "debit_amt",
      ) +
      getSelectedTotal(preparedBranchOutDr, branchOutDebitRows, "debit_amt");

    const difference = totalCheckedCredit - totalCheckedDebit;

    const totalCheckedRows =
      headOfficeOutCreditRows.length +
      branchOutCreditRows.length +
      headOfficeOutDebitRows.length +
      branchOutDebitRows.length;

    return {
      totalCheckedCredit,
      totalCheckedDebit,
      difference,
      totalCheckedRows,
    };
  }, [
    preparedHeadOfficeOutCr,
    preparedBranchOutCr,
    preparedHeadOfficeOutDr,
    preparedBranchOutDr,
    headOfficeOutCreditRows,
    branchOutCreditRows,
    headOfficeOutDebitRows,
    branchOutDebitRows,
  ]);

  // Function to handle checkbox click event
  const handleRowCheckboxChangeHeadOfficeOutCr = (index) => {
    const selected = [...headOfficeOutCreditRows];
    if (selected.includes(index)) {
      // Remove index if already selected
      setHeadOfficeOutCreditRows(selected.filter((id) => id !== index));
    } else {
      // Add index if not already selected
      setHeadOfficeOutCreditRows([...selected, index]);
    }
  };
  const handleRowCheckboxChangeHeadOfficeOutDr = (index) => {
    const selected = [...headOfficeOutDebitRows];
    if (selected.includes(index)) {
      // Remove index if already selected
      setHeadOfficeOutDebitRows(selected.filter((id) => id !== index));
    } else {
      // Add index if not already selected
      setHeadOfficeOutDebitRows([...selected, index]);
    }
  };
  // Function to handle checkbox click event
  const handleRowCheckboxChangeBranchOutCr = (index) => {
    const selected = [...branchOutCreditRows];
    if (selected.includes(index)) {
      // Remove index if already selected
      setBranchOutCreditRows(selected.filter((id) => id !== index));
    } else {
      // Add index if not already selected
      setBranchOutCreditRows([...selected, index]);
    }
  };
  const handleRowCheckboxChangeBranchOutDr = (index) => {
    const selected = [...branchOutDebitRows];
    if (selected.includes(index)) {
      // Remove index if already selected
      setBranchOutDebitRows(selected.filter((id) => id !== index));
    } else {
      // Add index if not already selected
      setBranchOutDebitRows([...selected, index]);
    }
  };
  const [deletedHistory, setDeletedHistory] = useState([]);
  // Deleting rows by their index
  const deleteSelectedRows = () => {
    const batch = [];

    headOfficeOutCreditRows.forEach((index) => {
      batch.push({
        type: "headOfficeOutCr",
        row: preparedHeadOfficeOutCr[index],
        index,
      });
    });

    headOfficeOutDebitRows.forEach((index) => {
      batch.push({
        type: "headOfficeOutDr",
        row: preparedHeadOfficeOutDr[index],
        index,
      });
    });

    branchOutCreditRows.forEach((index) => {
      batch.push({
        type: "branchOutCr",
        row: preparedBranchOutCr[index],
        index,
      });
    });

    branchOutDebitRows.forEach((index) => {
      batch.push({
        type: "branchOutDr",
        row: preparedBranchOutDr[index],
        index,
      });
    });

    if (batch.length === 0) return; // nothing selected

    // Add this batch to history (keep previous ones)
    setDeletedHistory((prev) => {
      const updated = [...prev, batch];
      return updated.slice(-10); // Keep only last 10 actions
    });

    // Filter out deleted rows
    const headOfficeOutCrUpdatedData = preparedHeadOfficeOutCr.filter(
      (row, index) => !headOfficeOutCreditRows.includes(index),
    );
    const headOfficeOutDrUpdatedData = preparedHeadOfficeOutDr.filter(
      (row, index) => !headOfficeOutDebitRows.includes(index),
    );
    const branchOutCrUpdatedData = preparedBranchOutCr.filter(
      (row, index) => !branchOutCreditRows.includes(index),
    );
    const branchOutDrUpdatedData = preparedBranchOutDr.filter(
      (row, index) => !branchOutDebitRows.includes(index),
    );

    // Update states
    setBranchOutCr(branchOutCrUpdatedData);
    setBranchOutDr(branchOutDrUpdatedData);
    setHeadOfficeOutCr(headOfficeOutCrUpdatedData);
    setHeadOfficeOutDr(headOfficeOutDrUpdatedData);

    // Clear selections
    setHeadOfficeOutCreditRows([]);
    setHeadOfficeOutDebitRows([]);
    setBranchOutDebitRows([]);
    setBranchOutCreditRows([]);
  };

  const undoPreviousAction = () => {
    if (deletedHistory.length === 0) {
      message.info("There are no previous actions");
      return;
    }

    // Get the most recent batch
    const lastBatch = deletedHistory[deletedHistory.length - 1];

    // Remove it from history
    setDeletedHistory((prev) => prev.slice(0, -1));

    // Restore each deleted row from that batch
    lastBatch.forEach((item) => {
      switch (item.type) {
        case "headOfficeOutCr":
          setHeadOfficeOutCr((prev) => {
            const newArr = [...prev];
            newArr.splice(item.index, 0, item.row);
            return newArr;
          });
          break;
        case "headOfficeOutDr":
          setHeadOfficeOutDr((prev) => {
            const newArr = [...prev];
            newArr.splice(item.index, 0, item.row);
            return newArr;
          });
          break;
        case "branchOutCr":
          setBranchOutCr((prev) => {
            const newArr = [...prev];
            newArr.splice(item.index, 0, item.row);
            return newArr;
          });
          break;
        case "branchOutDr":
          setBranchOutDr((prev) => {
            const newArr = [...prev];
            newArr.splice(item.index, 0, item.row);
            return newArr;
          });
          break;
        default:
          break;
      }
    });
  };

  const calculateTotalValueHeadOfficeOutCr = () => {
    let totalValue = 0;
    headOfficeOutCreditRows.forEach((index) => {
      const row = preparedHeadOfficeOutCr[index];
      totalValue += row.credit_amt;
    });
    return totalValue;
  };
  const calculateTotalValueHeadOfficeOutDr = () => {
    let totalValue = 0;
    headOfficeOutDebitRows.forEach((index) => {
      const row = preparedHeadOfficeOutDr[index];
      totalValue += row.debit_amt;
    });
    return totalValue;
  };
  const calculateTotalValueBranchOutCr = () => {
    let totalValue = 0;
    branchOutCreditRows.forEach((index) => {
      const row = preparedBranchOutCr[index];
      totalValue += row.credit_amt;
    });
    return totalValue;
  };
  const calculateTotalValueBranchOutDr = () => {
    let totalValue = 0;
    branchOutDebitRows.forEach((index) => {
      const row = preparedBranchOutDr[index];
      totalValue += row.debit_amt;
    });
    return totalValue;
  };

  const [expandingData, setExpandingData] = useState({});

  function handleExpandingData(el) {
    if (expandingData && expandingData === el) {
      setExpandingData({});
      return;
    }
    setExpandingData(el);
  }
  const rowRefs = React.useRef({});
  const lastFoundRowRef = React.useRef(null);

  // Statements can leave tens of thousands of outstanding rows, and drawing
  // them all at once freezes the browser, so the tables show one page at a time
  const ROWS_PER_PAGE_OPTIONS = [50, 100, 250, 500];
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [pages, setPages] = useState({});
  const [pendingScrollRow, setPendingScrollRow] = useState(null);

  const tableRows = {
    headOfficeOutCr: headOfficehOutCr,
    branchOutCr: branchOutCr,
    headOfficeOutDr: headOfficehOutDr,
    branchOutDr: branchOutDr,
    removed: sortedAutoRemovedItems,
  };
  const lastPage = (count) => Math.max(0, Math.ceil(count / rowsPerPage) - 1);
  const pageShown = (tableKey) =>
    Math.min(pages[tableKey] || 0, lastPage(tableRows[tableKey].length));
  const pageStart = (tableKey) => pageShown(tableKey) * rowsPerPage;
  const pageOf = (tableKey) =>
    tableRows[tableKey].slice(
      pageStart(tableKey),
      pageStart(tableKey) + rowsPerPage,
    );

  // Opens the page holding the row, then scrolls to it once it is drawn
  function showRow(tableKey, index) {
    setPages((prev) => ({
      ...prev,
      [tableKey]: Math.floor(index / rowsPerPage),
    }));
    setPendingScrollRow(`${tableKey}-${index}`);
  }

  React.useEffect(() => {
    if (!pendingScrollRow) return;
    rowRefs.current[pendingScrollRow]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    setPendingScrollRow(null);
  }, [pendingScrollRow, pages]);

  function showMatchingRow(tableKey, amountKey, amount) {
    const index = tableRows[tableKey].findIndex(
      (row) => Number(row[amountKey]) === Number(amount),
    );
    if (index !== -1) showRow(tableKey, index);
  }

  function renderPager(tableKey) {
    const count = tableRows[tableKey].length;
    if (count <= ROWS_PER_PAGE_OPTIONS[0]) return null;
    return (
      <TablePagination
        component="div"
        count={count}
        page={pageShown(tableKey)}
        onPageChange={(e, newPage) =>
          setPages((prev) => ({ ...prev, [tableKey]: newPage }))
        }
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setPages({});
        }}
      />
    );
  }

  function renderPagerRow(tableKey) {
    const pager = renderPager(tableKey);
    if (!pager) return null;
    return (
      <TableRow>
        <TableCell
          colSpan={7}
          style={{ position: "static", backgroundColor: "#fff", padding: 0 }}
        >
          {pager}
        </TableCell>
      </TableRow>
    );
  }
  const [highlightedDifferenceRow, setHighlightedDifferenceRow] =
    React.useState(null);

  const normalizeMoney = (value) => {
    const number = Number(value || 0);
    if (Number.isNaN(number)) return 0;
    return Math.round(number * 100) / 100;
  };

  const sameMoney = (a, b) => {
    return Math.abs(normalizeMoney(a) - normalizeMoney(b)) < 0.01;
  };

  const setDifferenceRowRef = (tableKey, index) => (node) => {
    if (node) {
      rowRefs.current[`${tableKey}-${index}`] = node;
    } else {
      delete rowRefs.current[`${tableKey}-${index}`];
    }
  };

  const getDifferenceHighlightStyle = (rowKey, el) => {
    const isFoundDifferenceRow = highlightedDifferenceRow === rowKey;

    return {
      backgroundColor: isFoundDifferenceRow
        ? "#fff7cc"
        : el === expandingData
          ? "#e2e8f0"
          : "",
      boxShadow: isFoundDifferenceRow ? "inset 0 0 0 2px #f59e0b" : "none",
      transition: "all 0.25s ease",
    };
  };

  React.useEffect(() => {
    const timer = window.setTimeout(() => {
      const difference = normalizeMoney(checkedSummary.difference);

      if (checkedSummary.totalCheckedRows === 0 || sameMoney(difference, 0)) {
        setHighlightedDifferenceRow(null);
        lastFoundRowRef.current = null;
        return;
      }

      const targetAmount = Math.abs(difference);

      const debitTables = [
        {
          tableKey: "headOfficeOutDr",
          label: "OUR DEBITS",
          rows: preparedHeadOfficeOutDr,
          amountKey: "debit_amt",
          selectedRows: headOfficeOutDebitRows,
        },
        {
          tableKey: "branchOutDr",
          label: "THEIR DEBITS",
          rows: preparedBranchOutDr,
          amountKey: "debit_amt",
          selectedRows: branchOutDebitRows,
        },
      ];

      const creditTables = [
        {
          tableKey: "headOfficeOutCr",
          label: "OUR CREDITS",
          rows: preparedHeadOfficeOutCr,
          amountKey: "credit_amt",
          selectedRows: headOfficeOutCreditRows,
        },
        {
          tableKey: "branchOutCr",
          label: "THEIR CREDITS",
          rows: preparedBranchOutCr,
          amountKey: "credit_amt",
          selectedRows: branchOutCreditRows,
        },
      ];

      const tablesToSearch = difference > 0 ? debitTables : creditTables;

      let found = null;

      for (const table of tablesToSearch) {
        const foundIndex = table.rows?.findIndex((row, rowIndex) => {
          const rowIsAlreadyChecked = table.selectedRows.includes(rowIndex);

          return (
            !rowIsAlreadyChecked &&
            sameMoney(row?.[table.amountKey], targetAmount)
          );
        });

        if (foundIndex !== -1) {
          found = {
            tableKey: table.tableKey,
            label: table.label,
            index: foundIndex,
            amount: targetAmount,
          };
          break;
        }
      }

      if (!found) {
        setHighlightedDifferenceRow(null);
        lastFoundRowRef.current = null;
        return;
      }

      const rowKey = `${found.tableKey}-${found.index}`;
      setHighlightedDifferenceRow(rowKey);

      if (lastFoundRowRef.current !== rowKey) {
        showRow(found.tableKey, found.index);

        message.success(
          `Difference ${targetAmount.toLocaleString()} found in ${found.label}`,
        );

        lastFoundRowRef.current = rowKey;
      }
    }, 120);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    checkedSummary.difference,
    checkedSummary.totalCheckedRows,
    preparedHeadOfficeOutCr,
    preparedBranchOutCr,
    preparedHeadOfficeOutDr,
    preparedBranchOutDr,
    headOfficeOutCreditRows,
    branchOutCreditRows,
    headOfficeOutDebitRows,
    branchOutDebitRows,
  ]);
  function swapHeadOfficeOutstandingCredit(main, swap) {
    // Find the index of 'main' in branchOutDr array
    const mainIndexInHeadOfficeOutCr = headOfficehOutCr.findIndex(
      (d) =>
        d.credit_amt === main.credit_amt && d.description === main.description,
    );

    // Find the index of 'swap' in b_dr_final_with_prev array
    const swapIndexInBDrFinalWithPrev = ho_cr_final_with_prev.findIndex(
      (d) =>
        d.credit_amt === swap.credit_amt && d.description === swap.description,
    );

    // If both items are found in their respective arrays
    if (
      mainIndexInHeadOfficeOutCr !== -1 &&
      swapIndexInBDrFinalWithPrev !== -1
    ) {
      // Create a new copy of branchOutDr with the swapped item in the same position
      const newHeadOfficeOutCr = [...headOfficehOutCr];
      newHeadOfficeOutCr[mainIndexInHeadOfficeOutCr] = swap;

      // Create a new copy of b_dr_final_with_prev with the swapped item in the same position
      const newBDrFinalWithPrev = [...ho_cr_final_with_prev];
      newBDrFinalWithPrev[swapIndexInBDrFinalWithPrev] = main;

      // Update the states with new arrays
      setHeadOfficeOutCr(newHeadOfficeOutCr);
      ho_cr_final_with_prev = newBDrFinalWithPrev;

      // Notify the user of success
      toast.success("Successfully swapped");
    } else {
      // Handle case where either item is not found (if needed)
      toast.error("Item not found for swapping");
    }
  }
  function swapBranchOutstandingCredit(main, swap) {
    // Find the index of 'main' in branchOutDr array
    const mainIndexInBranchOutCr = branchOutCr.findIndex(
      (d) =>
        d.credit_amt === main.credit_amt && d.description === main.description,
    );
    // Find the index of 'swap' in b_dr_final_with_prev array
    const swapIndexInBDrFinalWithPrev = b_cr_final_with_prev.findIndex(
      (d) =>
        d.credit_amt === swap.credit_amt && d.description === swap.description,
    );

    // If both items are found in their respective arrays
    if (mainIndexInBranchOutCr !== -1 && swapIndexInBDrFinalWithPrev !== -1) {
      // Create a new copy of branchOutDr with the swapped item in the same position
      const newBranchOutCr = [...branchOutCr];
      newBranchOutCr[mainIndexInBranchOutCr] = swap;

      // Create a new copy of b_dr_final_with_prev with the swapped item in the same position
      const newBDrFinalWithPrev = [...b_cr_final_with_prev];
      newBDrFinalWithPrev[swapIndexInBDrFinalWithPrev] = main;

      // Update the states with new arrays
      setBranchOutCr(newBranchOutCr);
      b_cr_final_with_prev = newBDrFinalWithPrev;

      // Notify the user of success
      toast.success("Successfully swapped");
    } else {
      // Handle case where either item is not found (if needed)
      toast.error("Item not found for swapping");
    }
  }

  function swapHeadOfficeOutStandingDebit(main, swap) {
    // Find the index of 'main' in branchOutDr array
    const mainIndexInBranchOutDr = headOfficehOutDr.findIndex(
      (d) =>
        d.debit_amt === main.debit_amt && d.description === main.description,
    );

    // Find the index of 'swap' in b_dr_final_with_prev array
    const swapIndexInBDrFinalWithPrev = ho_dr_final_with_prev.findIndex(
      (d) =>
        d.debit_amt === swap.debit_amt && d.description === swap.description,
    );

    // If both items are found in their respective arrays
    if (mainIndexInBranchOutDr !== -1 && swapIndexInBDrFinalWithPrev !== -1) {
      // Create a new copy of branchOutDr with the swapped item in the same position
      const newHeadOfficeDr = [...headOfficehOutDr];
      newHeadOfficeDr[mainIndexInBranchOutDr] = swap;

      // Create a new copy of b_dr_final_with_prev with the swapped item in the same position
      const newBDrFinalWithPrev = [...ho_dr_final_with_prev];
      newBDrFinalWithPrev[swapIndexInBDrFinalWithPrev] = main;

      // Update the states with new arrays
      setHeadOfficeOutDr(newHeadOfficeDr);
      ho_dr_final_with_prev = newBDrFinalWithPrev;

      // Notify the user of success
      toast.success("Successfully swapped");
    } else {
      // Handle case where either item is not found (if needed)
      toast.error("Item not found for swapping");
    }
  }

  function swapBranchOutStandingDebit(main, swap) {
    // Find the index of 'main' in branchOutDr array
    const mainIndexInBranchOutDr = branchOutDr.findIndex(
      (d) =>
        d.debit_amt === main.debit_amt && d.description === main.description,
    );

    // Find the index of 'swap' in b_dr_final_with_prev array
    const swapIndexInBDrFinalWithPrev = b_dr_final_with_prev.findIndex(
      (d) =>
        d.debit_amt === swap.debit_amt && d.description === swap.description,
    );

    // If both items are found in their respective arrays
    if (mainIndexInBranchOutDr !== -1 && swapIndexInBDrFinalWithPrev !== -1) {
      // Create a new copy of branchOutDr with the swapped item in the same position
      const newBranchOutDr = [...branchOutDr];
      newBranchOutDr[mainIndexInBranchOutDr] = swap;

      // Create a new copy of b_dr_final_with_prev with the swapped item in the same position
      const newBDrFinalWithPrev = [...b_dr_final_with_prev];
      newBDrFinalWithPrev[swapIndexInBDrFinalWithPrev] = main;

      // Update the states with new arrays
      setBranchOutDr(newBranchOutDr);
      b_dr_final_with_prev = newBDrFinalWithPrev;

      // Notify the user of success
      toast.success("Successfully swapped");
    } else {
      // Handle case where either item is not found (if needed)
      toast.error("Item not found for swapping");
    }
  }

  const [fileName, setFileName] = useState(null);
  const [branchFileName, setBranchFileName] = useState(null);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);

        // IMPORTANT: use "array" when passing a Uint8Array
        let workbook;
        if (file.name.endsWith(".csv")) {
          const text = new TextDecoder("utf-8").decode(data);
          workbook = XLSX.read(text, { type: "string" });
        } else {
          workbook = XLSX.read(data, { type: "array" });
        }

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          toast.error("No sheets found in workbook");
          return;
        }

        // Read the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Read raw data as array of arrays; defval ensures missing cells are empty strings
        const allRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        // Defensive: ensure we have rows
        if (!Array.isArray(allRows) || allRows.length === 0) {
          toast.error("No rows found in sheet");
          return;
        }

        // 🔍 Find header row index robustly (ignore null/undefined rows)
        const headerRowIndex = allRows.findIndex(
          (row) =>
            Array.isArray(row) &&
            row.some((cell) => {
              if (cell === null || cell === undefined) return false;
              return String(cell).toLowerCase().trim() === "value date";
            }),
        );

        if (headerRowIndex === -1) {
          toast.error("Could not find 'Value Date' column in this file.");
          return;
        }

        // 🪓 Trim all rows before header
        const trimmedRows = allRows.slice(headerRowIndex);

        // ✅ Validate required headers (case insensitive)
        const headers = trimmedRows[0].map((h) =>
          String(h).toLowerCase().trim(),
        );
        const requiredHeaders = [
          "value date",
          "description",
          "debit amt",
          "credit amt",
        ];
        const missingHeaders = requiredHeaders.filter(
          (req) => !headers.includes(req),
        );

        if (missingHeaders.length > 0) {
          toast.error(
            `Missing required column(s): ${missingHeaders.join(", ")}`,
          );
          return;
        }
        // If only header row exists, warn
        if (trimmedRows.length <= 1) {
        }

        // Convert to sheet and then to JSON using defval to keep empty cells
        const cleanSheet = XLSX.utils.aoa_to_sheet(trimmedRows);
        const jsonData = XLSX.utils.sheet_to_json(cleanSheet, {
          defval: "",
          raw: false,
        });

        // 🧽 Extra cleaning — similar to your CSV logic
        const cleanedData = cleanData(jsonData);
        setFileName(file.name);
        setHeadOfficeData(cleanedData);
        message.info("Primary Data Loaded");
      } catch (err) {
        toast.error("There was an error processing the file.");
      }
    };

    reader.readAsArrayBuffer(file);
  };
  const handleBranchFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);

        // IMPORTANT: use "array" when passing a Uint8Array
        let workbook;
        if (file.name.endsWith(".csv")) {
          const text = new TextDecoder("utf-8").decode(data);
          workbook = XLSX.read(text, { type: "string" });
        } else {
          workbook = XLSX.read(data, { type: "array" });
        }

        if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
          toast.error("No sheets found in workbook");
          return;
        }

        // Read the first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Read raw data as array of arrays; defval ensures missing cells are empty strings
        const allRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: "",
        });

        // Defensive: ensure we have rows
        if (!Array.isArray(allRows) || allRows.length === 0) {
          toast.error("No rows found in sheet");
          return;
        }

        // 🔍 Find header row index robustly (ignore null/undefined rows)
        const headerRowIndex = allRows.findIndex(
          (row) =>
            Array.isArray(row) &&
            row.some((cell) => {
              if (cell === null || cell === undefined) return false;
              return String(cell).toLowerCase().trim() === "value date";
            }),
        );

        if (headerRowIndex === -1) {
          toast.error("Could not find 'Value Date' column in this file.");
          return;
        }

        // 🪓 Trim all rows before header
        const trimmedRows = allRows.slice(headerRowIndex);

        // If only header row exists, warn
        if (trimmedRows.length <= 1) {
          toast.error("No data rows found after the header row.");
        }

        // ✅ Validate required headers (case insensitive)
        const headers = trimmedRows[0].map((h) =>
          String(h).toLowerCase().trim(),
        );
        const requiredHeaders = [
          "value date",
          "description",
          "debit amt",
          "credit amt",
        ];
        const missingHeaders = requiredHeaders.filter(
          (req) => !headers.includes(req),
        );

        if (missingHeaders.length > 0) {
          toast.error(
            `Missing required column(s): ${missingHeaders.join(", ")}`,
          );
          return;
        }

        // Convert to sheet and then to JSON using defval to keep empty cells
        const cleanSheet = XLSX.utils.aoa_to_sheet(trimmedRows);
        const jsonData = XLSX.utils.sheet_to_json(cleanSheet, {
          defval: "",
          raw: false,
        });

        // 🧽 Extra cleaning — similar to your CSV logic
        const cleanedData = cleanData(jsonData);
        setBranchFileName(file.name);
        setBranchData(cleanedData);
        message.info("Secondary Data Loaded");
      } catch (err) {
        toast.error("There was an error processing the file");
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleHeadOfficeRemoveFile = () => {
    setFileName(null);
    setHeadOfficeData([]);
    setTrigger(false);
    setAutoRemovedItems([]);
    setSelectedAutoRemovedRows([]);
    setRemovedItemsOpen(false);
    if (primaryFileInputRef.current) {
      primaryFileInputRef.current.value = null;
    }
  };
  const handleBranchRemoveFile = () => {
    setBranchFileName(null);
    setBranchData([]);
    setTrigger(false);
    setAutoRemovedItems([]);
    setSelectedAutoRemovedRows([]);
    setRemovedItemsOpen(false);
    if (branchFileInputRef.current) {
      branchFileInputRef.current.value = null;
    }
  };
  const cleanData = (data) => {
    const jsonObject = [];

    const headers = Object.keys(data[0] || {}).map((h) =>
      String(h).toLowerCase().trim().replace(/\s+/g, "_"),
    );

    // Helper: convert Excel serial number → date string (e.g., "4-Jan-22")
    const excelSerialToDate = (serial) => {
      if (serial === null || serial === undefined || serial === "") return "";
      if (typeof serial === "string") {
        // If it's already a readable date like "4-Jan-22", just return it
        if (/\d{1,2}-[A-Za-z]{3}-\d{2}/.test(serial)) return serial.trim();
        const num = parseFloat(serial);
        if (!isNaN(num)) serial = num;
        else return serial;
      }
      if (typeof serial !== "number") return serial;

      const excelEpoch = new Date(1899, 11, 30);
      const result = new Date(excelEpoch.getTime() + serial * 86400000);
      const day = result.getDate();
      const month = result.toLocaleString("en-GB", { month: "short" });
      const year = result.getFullYear().toString().slice(-2);
      return `${day}-${month}-${year}`;
    };

    // Helper: clean numeric fields (remove commas, - , quotes, spaces etc.)
    const cleanNumber = (raw) => {
      if (raw === null || raw === undefined || raw === "") return 0;
      let s = String(raw).trim();
      s = s
        .replace(/^['"]+|['"]+$/g, "") // remove leading/trailing quotes
        .replace(/,/g, "") // remove commas
        .replace(/-/g, "") // remove hyphens
        .replace(/\s+/g, ""); // remove spaces
      if (s === "" || s === ".") return 0;
      const n = parseFloat(s);
      return Number.isNaN(n) ? 0 : n;
    };

    // Process each row
    data.forEach((row) => {
      const cleanedRow = {};

      headers.forEach((key) => {
        // row might contain keys exactly matching headers; if not, try to find by position
        let value = row[key];

        // Fallback: if row doesn't have key (rare with sheet_to_json), try positional access
        if (value === undefined) {
          // find index of this header in the header row from the original data
          // (not always necessary; keeping for safety)
          const headerIndex = headers.indexOf(key);
          if (headerIndex > -1) {
            value = Object.values(row)[headerIndex] ?? "";
          } else {
            value = "";
          }
        }

        if (key === "value_date" || key === "post_date") {
          value = excelSerialToDate(value);
        } else if (
          key === "debit_amt" ||
          key === "credit_amt" ||
          key === "balance"
        ) {
          value = cleanNumber(value);
        } else if (typeof value === "string") {
          value = value.replace(/,/g, " ").trim();
        }

        cleanedRow[key] = value;
      });

      // Keep only rows with a valid value_date
      jsonObject.push(cleanedRow);
    });

    // Merge multiline descriptions
    let previousTransactionIndex = null;
    jsonObject.forEach((val, index) => {
      if (!val.value_date) {
        if (previousTransactionIndex !== null) {
          jsonObject[previousTransactionIndex].description =
            (jsonObject[previousTransactionIndex].description || "") +
            " " +
            (val.description || "");
        }
      } else {
        previousTransactionIndex = index;
      }
    });

    // Filter valid rows (value_date truthy)
    return jsonObject.filter(
      (row) => row.value_date && String(row.value_date).trim() !== "",
    );
  };

  // sorting head office outstanding
  headOfficehOutCr.sort((a, b) => b.credit_amt - a.credit_amt);
  headOfficehOutDr.sort((a, b) => b.debit_amt - a.debit_amt);

  // sorting branch outstanding
  branchOutCr.sort((a, b) => b.credit_amt - a.credit_amt);
  branchOutDr.sort((a, b) => b.debit_amt - a.debit_amt);

  // Amounts of each table, built once per table instead of scanned per row
  const amountSetCache = React.useRef(new WeakMap());
  function amountsOf(arr, amountKey) {
    let amounts = amountSetCache.current.get(arr);
    if (!amounts) {
      amounts = new Set(arr.map((item) => Number(item[amountKey])));
      amountSetCache.current.set(arr, amounts);
    }
    return amounts;
  }

  function existsInBothArrays(creditArray, debitArray, amount) {
    const foundInCredit = amountsOf(creditArray, "credit_amt").has(
      Number(amount),
    );

    const foundInDebit = amountsOf(debitArray, "debit_amt").has(Number(amount));

    return foundInCredit && foundInDebit ? "yes" : "";
  }

  const sendDataToServer = async () => {
    const { branch, submission_date, primary_balance } = submissionData || {};

    if (!branch || !submission_date || primary_balance == null) {
      toast.error(
        "Please make sure all these fields are filled: primary location, secondary location, date, and branch balance.",
      );
      return;
    }

    setSubmitting(true);
    let loadingId;

    try {
      const payload = {
        credits: sendingCreditDataModified,
        debits: sendingDebitDataModified,
        submission: submissionData,
      };

      loadingId = toast.loading("submitting data...");
      await API.post("/api/v1/reconciliation-submission", payload);

      toast.remove(loadingId);
      toast.success("Reconciliation submitted successfully");

      // branch
      setBranchOutDr([]);
      setBranchOutCr([]);
      // head office
      setHeadOfficeOutCr([]);
      setHeadOfficeOutDr([]);
      setBranchFileName(null);
      setFileName(null);
      setTrigger(false);
      setOpen(false);
      setAutoRemovedItems([]);
      setSelectedAutoRemovedRows([]);
      setRemovedItemsOpen(false);

    } catch (err) {
      toast.remove(loadingId);
      const status = err.response?.status;

      if (status === 409) {
        toast.error(err.response?.data?.message || "Data already exists");
      } else {
        toast.error("Something went wrong while submitting data");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="scroll-container"
      style={{ paddingBottom: 40 }}
    >
      <Modal
        open={open}
        title="Please Confirm Submission"
        onCancel={handleCancel}
        footer={() => (
          <>
            <Button
              onClick={(e) => {
                e.preventDefault();
                sendDataToServer();
              }}
            >
              Submit
            </Button>
            <Button style={{ color: "red" }} onClick={handleCancel}>
              Cancel
            </Button>
          </>
        )}
      >
        <p>
          By submitting, you confirm that you have reviewed all items and
          acknowledge that they are outstanding.
        </p>
      </Modal>
      <Modal
        open={removedItemsOpen}
        title="System Auto Removed Items"
        onCancel={closeRemovedItemsModal}
        footer={() => (
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              gap: 2,
              flexWrap: "wrap",
              zIndex: 200000000,
            }}
          >
            <Typography style={{ fontSize: "12px" }}>
              Selected: <strong>{selectedAutoRemovedRows.length}</strong>
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Button
                variant="contained"
                onClick={restoreSelectedAutoRemovedItems}
                disabled={selectedAutoRemovedRows.length === 0}
              >
                Post Selected Back To Outstanding
              </Button>
              <Button onClick={closeRemovedItemsModal}>Close</Button>
            </Box>
          </Box>
        )}
        width={1050}
        zIndex={10000000}
      >
        {autoRemovedItems.length === 0 ? (
          <Typography>
            No auto removed items found for this reconciliation.
          </Typography>
        ) : (
          <>
            <Typography style={{ marginBottom: "10px", fontSize: "12px" }}>
              The records below are sorted by amount. You can select any
              transaction(s) and post them back to the outstanding tables when
              the amounts are the same but the descriptions are different.
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      padding="checkbox"
                      rowSpan={2}
                      style={{ fontWeight: 700, verticalAlign: "middle" }}
                    >
                      Select
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      style={{ fontWeight: 700, verticalAlign: "middle" }}
                    >
                      Grouped Amount
                    </TableCell>
                    <TableCell
                      rowSpan={2}
                      style={{ fontWeight: 700, verticalAlign: "middle" }}
                    >
                      Amount
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell style={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>Date</TableCell>
                    <TableCell style={{ fontWeight: 700 }}>
                      Description
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {pageOf("removed").map((item, i) => {
                    const index = pageStart("removed") + i;
                    const prevAmount =
                      index > 0
                        ? Number(sortedAutoRemovedItems[index - 1]?.amount || 0)
                        : null;

                    const currentAmount = Number(item.amount || 0);
                    const isNewAmountGroup =
                      index === 0 || prevAmount !== currentAmount;

                    const rowStyles = getTransactionTypeStyles(item.source);

                    return (
                      <React.Fragment key={item.id}>
                        {isNewAmountGroup && (
                          <TableRow>
                            <TableCell
                              colSpan={9}
                              style={{
                                background: "#f5f5f5",
                                fontWeight: 700,
                                fontSize: "13px",
                                color: "#262626",
                                borderTop: "2px solid #d9d9d9",
                              }}
                            >
                              Amount Group:{" "}
                              <span style={getAmountGroupBadgeStyle()}>
                                {currencyFormatter.format(currentAmount)}
                              </span>
                            </TableCell>
                          </TableRow>
                        )}

                        <TableRow
                          hover
                          style={{
                            ...rowStyles,
                            transition: "all 0.2s ease",
                          }}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={selectedAutoRemovedRows.includes(
                                item.id,
                              )}
                              onChange={() =>
                                toggleAutoRemovedSelection(item.id)
                              }
                            />
                          </TableCell>

                          <TableCell>
                            <span style={getAmountGroupBadgeStyle()}>
                              {currencyFormatter.format(currentAmount)}
                            </span>
                          </TableCell>

                          <TableCell
                            style={{ fontSize: "12px", fontWeight: 700 }}
                          >
                            {currencyFormatter.format(currentAmount)}
                          </TableCell>

                          <TableCell
                            style={{ fontSize: "12px", fontWeight: 600 }}
                          >
                            {item.source || "-"}
                          </TableCell>

                          <TableCell style={{ fontSize: "12px" }}>
                            {item.source_date
                              ? moment(item.source_date).format("DD-MM-YYYY")
                              : "-"}
                          </TableCell>

                          <TableCell
                            style={{ fontSize: "12px", fontWeight: 500 }}
                          >
                            {item.source_description || "-"}
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            {renderPager("removed")}
          </>
        )}
      </Modal>
      <Helmet title="New Reconciliation | Reconciliation" />
      <Box sx={{ mb: 1.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: colors.textPrimary }}>
          Reconcile two statements
        </Typography>
        <Typography sx={{ fontSize: 13.5, color: colors.textSecondary }}>
          Match the primary location&apos;s statement against the secondary
          location&apos;s and list what is still outstanding.
        </Typography>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1.5,
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        {/* primary location */}
        <FormControl sx={{ minWidth: 190 }} size="small">
          <InputLabel id="recon-primary-label">Primary location</InputLabel>
          <Select
            labelId="recon-primary-label"
            id="recon-primary"
            value={primary}
            label="Primary location"
            onChange={handleChangePrimary}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {locations?.map((data) => (
              <MenuItem key={data._id} value={data.name}>
                {data.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 190 }} size="small">
          <InputLabel id="recon-secondary-label">Secondary location</InputLabel>
          <Select
            labelId="recon-secondary-label"
            id="recon-secondary"
            value={secondary}
            label="Secondary location"
            onChange={handleChangeSecondary}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {locations?.map((data) => (
              <MenuItem key={data._id} value={data.name}>
                {data.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="date"
          label="As at"
          InputLabelProps={{ shrink: true }}
          value={data.date}
          onChange={(e) => setData({ ...data, date: e.target.value })}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1.5,
          p: 1.5,
          mb: 1.5,
          borderRadius: "14px",
          border: `1px solid ${colors.border}`,
          background: colors.surface,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Tooltip title="Import the primary location's statement (.xlsx, .xls or .csv)">
            <Button
              variant={fileName ? "outlined" : "contained"}
              disableElevation
              size="small"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                ...(fileName
                  ? { color: colors.primary, borderColor: colors.border }
                  : { backgroundColor: colors.primary }),
              }}
            >
              {fileName ? "Change primary file" : "Import primary file"}
              <input
                hidden
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleFileUpload}
                ref={primaryFileInputRef}
              />
            </Button>
          </Tooltip>
          <Typography
            noWrap
            title={fileName || ""}
            sx={{
              maxWidth: 220,
              fontSize: 13,
              color: fileName ? colors.textPrimary : colors.textSecondary,
            }}
          >
            {fileName || "No file chosen"}
          </Typography>
          {fileName && (
            <Button
              size="small"
              color="error"
              onClick={handleHeadOfficeRemoveFile}
              sx={{ textTransform: "none", minWidth: 0 }}
            >
              Remove
            </Button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <Tooltip title="Import the secondary location's statement (.xlsx, .xls or .csv)">
            <Button
              variant={branchFileName ? "outlined" : "contained"}
              disableElevation
              size="small"
              component="label"
              startIcon={<UploadFileIcon />}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                ...(branchFileName
                  ? { color: colors.primary, borderColor: colors.border }
                  : { backgroundColor: colors.primary }),
              }}
            >
              {branchFileName ? "Change secondary file" : "Import secondary file"}
              <input
                hidden
                type="file"
                accept=".xlsx, .xls, .csv"
                onChange={handleBranchFileUpload}
                ref={branchFileInputRef}
              />
            </Button>
          </Tooltip>
          <Typography
            noWrap
            title={branchFileName || ""}
            sx={{
              maxWidth: 220,
              fontSize: 13,
              color: branchFileName ? colors.textPrimary : colors.textSecondary,
            }}
          >
            {branchFileName || "No file chosen"}
          </Typography>
          {branchFileName && (
            <Button
              size="small"
              color="error"
              onClick={handleBranchRemoveFile}
              sx={{ textTransform: "none", minWidth: 0 }}
            >
              Remove
            </Button>
          )}
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
          }}
        >
          {loader ? (
            <div
              style={{
                position: "fixed", // makes it overlay the whole screen
                top: 0,
                left: 0,
                width: "100vw",
                height: "100vh",
                display: "flex", // enables flexbox
                flexDirection: "column", // stack text + loader vertically
                alignItems: "center", // center horizontally
                justifyContent: "center", // center vertically
                backgroundColor: "rgba(0, 0, 0, 0.4)", // optional dim background
                zIndex: 9999, // make sure it’s on top
              }}
            >
              <strong style={{ color: "#fff", marginBottom: "10px" }}>
                Bringing in outstanding items from the last reconciliation…
              </strong>
              <MutatingDots
                height="70"
                width="100"
                color="#F5AF14"
                secondaryColor="#1F354D"
                radius="12.5"
                ariaLabel="mutating-dots-loading"
                visible={true}
              />
            </div>
          ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "10px",
              }}
            >
              <Button
                onClick={() => setRemovedItemsOpen(true)}
                variant="outlined"
                size="small"
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                }}
                disabled={autoRemovedItems.length === 0}
              >
                Review matched items ({autoRemovedItems.length})
              </Button>
              <Button
                onClick={reconcile}
                disableElevation
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  borderRadius: "10px",
                  px: 3,
                  backgroundColor: colors.secondary,
                  "&:hover": { backgroundColor: colors.secondaryDark },
                }}
                variant="contained"
                size="small"
              >
                Reconcile
              </Button>
            </div>
          )}
        </div>
      </Box>
      {trigger && (
        <Paper
          elevation={0}
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1200,
            maxWidth: "calc(100vw - 32px)",
            px: 1.5,
            py: 0.75,
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            borderRadius: "14px",
            border: `1px solid ${colors.border}`,
            backgroundColor: "rgba(255,255,255,0.96)",
            boxShadow: "0 12px 32px rgba(15, 23, 42, 0.18)",
          }}
        >
          <Tooltip title="Undo the last removal">
            <IconButton
              size="small"
              aria-label="Undo the last removal"
              onClick={undoPreviousAction}
            >
              <UndoIcon />
            </IconButton>
          </Tooltip>
          <Typography sx={{ fontSize: 13 }}>
            <strong>Balance per our books: </strong>
            {balancePerTheirBook
              ? currencyFormatter.format(balancePerOurBook)
              : "—"}
          </Typography>
          {Number(balancePerMyBook) > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              {balancePerOurBook.toFixed(2) * 1 === balancePerMyBook * 1 ? (
                <CheckBoxIcon sx={{ color: colors.secondary }} />
              ) : (
                <ErrorIcon sx={{ color: colors.danger }} />
              )}
              <Typography sx={{ fontSize: 13 }}>
                Difference:{" "}
                <strong>
                  {(
                    Number(balancePerMyBook) - Number(balancePerOurBook)
                  ).toFixed(2)}
                </strong>
              </Typography>
            </Stack>
          )}
          {(headOfficeOutCreditRows.length >= 1 ||
            branchOutCreditRows.length >= 1 ||
            headOfficeOutDebitRows.length >= 1 ||
            branchOutDebitRows.length >= 1) && (
            <Tooltip title="Take the ticked rows off the statement. Undo brings them back.">
              <Button
                onClick={deleteSelectedRows}
                variant="contained"
                color="error"
                disableElevation
                size="small"
                sx={{ textTransform: "none", fontWeight: 700, borderRadius: "10px" }}
              >
                Remove ticked (
                {headOfficeOutCreditRows.length +
                  branchOutCreditRows.length +
                  headOfficeOutDebitRows.length +
                  branchOutDebitRows.length}
                )
              </Button>
            </Tooltip>
          )}
          {(() => {
            const balanced =
              balancePerOurBook.toFixed(2) * 1 ===
              Number(balancePerMyBook).toFixed(2) * 1;
            const reason = !selected
              ? "Choose Debit or Credit account first"
              : !balancePerTheirBook || !balancePerMyBook
                ? "Enter both balances first"
                : "Balance per our books must equal your book balance (difference 0.00)";
            return (
              <Tooltip title={balanced ? "" : reason}>
                <span>
                  <Button
                    disabled={submitting || !balanced}
                    onClick={showModal}
                    size="small"
                    variant="contained"
                    disableElevation
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: "10px",
                      px: 2.5,
                      backgroundColor: colors.secondary,
                      "&:hover": { backgroundColor: colors.secondaryDark },
                    }}
                  >
                    Submit
                  </Button>
                </span>
              </Tooltip>
            );
          })()}
        </Paper>
      )}
      {trigger && !selected && (
        <Alert severity="info" sx={{ mb: 1.5, borderRadius: "12px" }}>
          Choose whether this is a <strong>Debit</strong> or{" "}
          <strong>Credit</strong> account (beside &quot;Balance as per their
          books&quot; below) to show the outstanding items.
        </Alert>
      )}
      {trigger ? (
        <Paper style={{ marginTop: "10px" }}>
          <TableContainer style={{ paddingBottom: "90px" }} component={Paper}>
            <Table
              sx={{
                tableLayout: "fixed",
                width: "100%",

                "& td, & th": {
                  padding: "6px 6px",
                  verticalAlign: "middle",
                },

                "& tr > *:nth-of-type(2)": {
                  whiteSpace: "nowrap",
                },

                "& tr > *:nth-of-type(3)": {
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                },

                "& tr > *:nth-of-type(4)": {
                  whiteSpace: "nowrap",
                  textAlign: "right",
                },

                "& tr > *:nth-of-type(5)": {
                  whiteSpace: "nowrap",
                  textAlign: "center",
                },

                "& tr > *:last-child": {
                  position: "sticky",
                  right: 0,
                  bgcolor: "#ccc",
                },
              }}
              size="small"
              aria-label="a dense table"
            >
              <colgroup>
                <col style={{ width: "52px" }} />
                <col style={{ width: "105px" }} />
                <col style={{ width: "360px" }} />
                <col style={{ width: "105px" }} />
                <col style={{ width: "70px" }} />
                <col style={{ width: "82px" }} />
                <col style={{ width: "82px" }} />
              </colgroup>

              <TableHead
                style={{ backgroundColor: "#fff", fontWeight: "bolder" }}
              >
                <TableRow style={{ fontWeight: "bolder" }}>
                  <TableCell
                    style={{
                      fontSize: "12px",
                      fontWeight: "bolder",
                      textTransform: "uppercase",
                    }}
                    align="center"
                    colSpan={7}
                  >
                    RECONCILIATION STATEMENT WITH{" "}
                    {secondary ? secondary : "[BRANCH NAME]"}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{ fontSize: "12px", fontWeight: "bold" }}
                  >
                    Balance As Per Their Books
                  </TableCell>

                  <TableCell colSpan={4} style={{ fontSize: "12px" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "6px",
                        whiteSpace: "nowrap",
                        overflow: "visible",
                      }}
                    >
                      <TextField
                        size="small"
                        type="number"
                        value={balancePerTheirBook}
                        onChange={(e) => setBalancePerTheirBook(e.target.value)}
                        placeholder="Their balance"
                        inputProps={{ "aria-label": "Balance as per their books" }}
                        sx={{ width: 170, "& input": { py: 0.6, fontSize: 13 } }}
                      />

                      <RadioGroup
                        row
                        aria-label="Account type"
                        value={checked ? "debit" : checked2 ? "credit" : ""}
                        sx={{
                          ml: 1,
                          px: 1,
                          borderRadius: "10px",
                          border: `1px solid ${selected ? colors.border : "#f59e0b"}`,
                          background: selected ? "transparent" : "#fffbeb",
                        }}
                      >
                        <FormControlLabel
                          value="debit"
                          control={
                            <Radio size="small" onChange={handleCheckedChange} />
                          }
                          label={<span style={{ fontSize: 12 }}>Debit account</span>}
                          sx={{ mr: 1 }}
                        />
                        <FormControlLabel
                          value="credit"
                          control={
                            <Radio size="small" onChange={handleCheckedChange2} />
                          }
                          label={<span style={{ fontSize: 12 }}>Credit account</span>}
                          sx={{ mr: 0 }}
                        />
                      </RadioGroup>
                    </div>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell
                    colSpan={3}
                    style={{ fontSize: "12px", fontWeight: "bold" }}
                  >
                    Balance As Per Our Books
                  </TableCell>

                  <TableCell colSpan={4} style={{ fontSize: "12px" }}>
                    <TextField
                      size="small"
                      type="number"
                      placeholder="Your book balance"
                      inputProps={{ "aria-label": "Balance as per our books" }}
                      sx={{ width: 170, "& input": { py: 0.6, fontSize: 13 } }}
                      value={balancePerMyBook}
                      onChange={(e) => setBalancePerMyBook(e.target.value)}
                    />
                  </TableCell>
                </TableRow>

                <TableRow style={{ backgroundColor: "#F5AF14" }}>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    SELECT
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    DATE
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    DESCRIPTION
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    OUTSTANDING
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    AGING
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    ACTION
                  </TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    CHECK
                  </TableCell>
                </TableRow>

                {/* OUR OUTSTANDING CREDIT */}
                <TableRow style={{ backgroundColor: "#ccc" }}>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      checked ? (
                        "ADD: OUR OUTSTANDING CREDIT"
                      ) : (
                        "LESS: OUR OUTSTANDING CREDIT"
                      )
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>
                    <a href="#myDebits">
                      <Button
                        style={{ color: "red", fontSize: "11px" }}
                        variant="outlined"
                        size="small"
                      >
                        OUR DEBITS
                      </Button>
                    </a>
                    <a href="#theirCredits">
                      <Button
                        variant="outlined"
                        size="small"
                        style={{ fontSize: "11px" }}
                      >
                        THEIR CREDIT
                      </Button>
                    </a>
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {pageOf("headOfficeOutCr").map((el, i) => {
                  const index = pageStart("headOfficeOutCr") + i;
                  const rowKey = `headOfficeOutCr-${index}`;

                  return (
                    <React.Fragment key={el.id || rowKey}>
                      <TableRow
                        ref={setDifferenceRowRef("headOfficeOutCr", index)}
                        id="myCredits"
                        style={getDifferenceHighlightStyle(rowKey, el)}
                        className={
                          headOfficeOutCreditRows.includes(index)
                            ? "selected"
                            : ""
                        }
                      >
                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            <Checkbox
                              size="small"
                              checked={headOfficeOutCreditRows.includes(index)}
                              onChange={() =>
                                handleRowCheckboxChangeHeadOfficeOutCr(index)
                              }
                            />
                          ) : (
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            moment(el.value_date).format("DD-MM-YYYY")
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            el.description
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell
                          id={`myCredit${el.credit_amt}`}
                          style={{ fontSize: "12px" }}
                        >
                          {selected ? (
                            currencyFormatter.format(el.credit_amt)
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            data.date ? (
                              ageing(el.value_date, data.date)
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        {filterOutOutstanding(ho_cr_final_with_prev, el)
                          .length > 0 ? (
                          <TableCell style={{ fontSize: "12px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandingData(el)}
                            >
                              {el === expandingData ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon />
                              )}
                            </IconButton>

                            <Tooltip title="reconciled items">
                              <IconButton size="small">
                                <Typography fontSize="12px" color="red">
                                  {
                                    filterOutOutstanding(
                                      ho_cr_final_with_prev,
                                      el,
                                    ).length
                                  }
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell></TableCell>
                        )}

                        <TableCell style={{ cursor: "pointer" }}>
                          <a
                            href={`#myDebit${el.credit_amt}`}
                            onClick={(e) => {
                              e.preventDefault();
                              showMatchingRow(
                                "headOfficeOutDr",
                                "debit_amt",
                                el.credit_amt,
                              );
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            {existsInBothArrays(
                              headOfficehOutCr,
                              headOfficehOutDr,
                              el.credit_amt,
                            )}
                          </a>
                        </TableCell>
                      </TableRow>

                      {el === expandingData && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>CREDITS</strong> The following credits
                              reconciled with the debits below{" "}
                              <ArrowDownwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {filterOutOutstanding(ho_cr_final_with_prev, el).map(
                            (sub, subIndex) => (
                              <TableRow
                                key={`ho-cr-sub-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#fdf2f8" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.credit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  <Tooltip title="swap with main outstanding">
                                    <IconButton
                                      onClick={() =>
                                        swapHeadOfficeOutstandingCredit(el, sub)
                                      }
                                      size="small"
                                    >
                                      <SwapVertIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ),
                          )}

                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>DEBITS</strong> | The following debits
                              reconciled with the credits above{" "}
                              <ArrowUpwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {b_dr_final_with_prev
                            .filter(
                              (sub) =>
                                Number(sub.debit_amt) === Number(el.credit_amt),
                            )
                            .map((sub, subIndex) => (
                              <TableRow
                                key={`b-dr-match-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#e7e5e4" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.debit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                              </TableRow>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {renderPagerRow("headOfficeOutCr")}

                <TableRow>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    Total
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {calculateTotalValueHeadOfficeOutCr() > 0 &&
                      currencyFormatter.format(
                        calculateTotalValueHeadOfficeOutCr(),
                      )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      currencyFormatter.format(ourOutStandingCreditsTotal)
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {/* THEIR OUTSTANDING CREDIT */}
                <TableRow style={{ backgroundColor: "#ccc" }}>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      `${checked ? "ADD" : "LESS"}: THEIR OUTSTANDING CREDIT`
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>
                    <a href="#theirDebits">
                      <Button
                        variant="outlined"
                        style={{
                          fontSize: "11px",
                          color: "red",
                          border: "1px solid red",
                        }}
                        size="small"
                      >
                        THEIR DEBITS
                      </Button>
                    </a>
                    <a href="#myCredits">
                      <Button
                        style={{ fontSize: "11px" }}
                        variant="outlined"
                        size="small"
                      >
                        OUR CREDITS
                      </Button>
                    </a>
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {pageOf("branchOutCr").map((el, i) => {
                  const index = pageStart("branchOutCr") + i;
                  const rowKey = `branchOutCr-${index}`;

                  return (
                    <React.Fragment key={el.id || rowKey}>
                      <TableRow
                        ref={setDifferenceRowRef("branchOutCr", index)}
                        id="theirCredits"
                        style={getDifferenceHighlightStyle(rowKey, el)}
                        className={
                          branchOutCreditRows.includes(index) ? "selected" : ""
                        }
                      >
                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            <Checkbox
                              size="small"
                              checked={branchOutCreditRows.includes(index)}
                              onChange={() =>
                                handleRowCheckboxChangeBranchOutCr(index)
                              }
                            />
                          ) : (
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            moment(el.value_date).format("DD-MM-YYYY")
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            el.description
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell
                          id={`theirCredit${el.credit_amt}`}
                          style={{ fontSize: "12px" }}
                        >
                          {selected ? (
                            currencyFormatter.format(el.credit_amt)
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            data.date ? (
                              ageing(el.value_date, data.date)
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        {filterOutOutstanding(b_cr_final_with_prev, el).length >
                        0 ? (
                          <TableCell style={{ fontSize: "12px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandingData(el)}
                            >
                              {el === expandingData ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon />
                              )}
                            </IconButton>

                            <Tooltip title="reconciled items">
                              <IconButton size="small">
                                <Typography fontSize="12px" color="red">
                                  {
                                    filterOutOutstanding(
                                      b_cr_final_with_prev,
                                      el,
                                    ).length
                                  }
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell></TableCell>
                        )}

                        <TableCell style={{ cursor: "pointer" }}>
                          <a
                            href={`#theirDebit${el.credit_amt}`}
                            onClick={(e) => {
                              e.preventDefault();
                              showMatchingRow(
                                "branchOutDr",
                                "debit_amt",
                                el.credit_amt,
                              );
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            {existsInBothArrays(
                              branchOutCr,
                              branchOutDr,
                              el.credit_amt,
                            )}
                          </a>
                        </TableCell>
                      </TableRow>

                      {el === expandingData && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>CREDITS</strong> The following credits
                              reconciled with the debits below{" "}
                              <ArrowDownwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {filterOutOutstanding(b_cr_final_with_prev, el).map(
                            (sub, subIndex) => (
                              <TableRow
                                key={`b-cr-sub-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#fdf2f8" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.credit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  <Tooltip title="swap with main outstanding">
                                    <IconButton
                                      onClick={() =>
                                        swapBranchOutstandingCredit(el, sub)
                                      }
                                      size="small"
                                    >
                                      <SwapVertIcon />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                              </TableRow>
                            ),
                          )}

                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>DEBITS</strong> | The following debits
                              reconciled with the credits above{" "}
                              <ArrowUpwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {ho_dr_final_with_prev
                            .filter(
                              (sub) =>
                                Number(sub.debit_amt) === Number(el.credit_amt),
                            )
                            .map((sub, subIndex) => (
                              <TableRow
                                key={`ho-dr-match-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#e7e5e4" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.debit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                              </TableRow>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {renderPagerRow("branchOutCr")}

                <TableRow>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    Total
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {calculateTotalValueBranchOutCr() > 0 &&
                      currencyFormatter.format(
                        calculateTotalValueBranchOutCr(),
                      )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      currencyFormatter.format(theirOutStandingCreditsTotal)
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                </TableRow>

                {/* OUR OUTSTANDING DEBIT */}
                <TableRow style={{ backgroundColor: "#ccc" }}>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      `${checked ? "LESS" : "ADD"}: OUR OUTSTANDING DEBIT`
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>
                    <a href="#myCredits">
                      <Button
                        variant="outlined"
                        style={{ fontSize: "10px" }}
                        size="small"
                      >
                        OUR CREDITS
                      </Button>
                    </a>
                    <a href="#theirDebits">
                      <Button
                        variant="outlined"
                        style={{ fontSize: "10px", color: "red" }}
                        size="small"
                      >
                        THEIR DEBITS
                      </Button>
                    </a>
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {pageOf("headOfficeOutDr").map((el, i) => {
                  const index = pageStart("headOfficeOutDr") + i;
                  const rowKey = `headOfficeOutDr-${index}`;

                  return (
                    <React.Fragment key={el.id || rowKey}>
                      <TableRow
                        ref={setDifferenceRowRef("headOfficeOutDr", index)}
                        id="myDebits"
                        style={getDifferenceHighlightStyle(rowKey, el)}
                        className={
                          headOfficeOutDebitRows.includes(index)
                            ? "selected"
                            : ""
                        }
                      >
                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            <Checkbox
                              size="small"
                              checked={headOfficeOutDebitRows.includes(index)}
                              onChange={() =>
                                handleRowCheckboxChangeHeadOfficeOutDr(index)
                              }
                            />
                          ) : (
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            moment(el.value_date).format("DD-MM-YYYY")
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            el.description
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell
                          id={`myDebit${el.debit_amt}`}
                          style={{ fontSize: "12px" }}
                        >
                          {selected ? (
                            currencyFormatter.format(el.debit_amt)
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            data.date ? (
                              ageing(el.value_date, data.date)
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        {filterOutOutstandingDebit(ho_dr_final_with_prev, el)
                          .length > 0 ? (
                          <TableCell style={{ fontSize: "12px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandingData(el)}
                            >
                              {el === expandingData ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon />
                              )}
                            </IconButton>

                            <Tooltip title="reconciled items">
                              <IconButton size="small">
                                <Typography fontSize="12px" color="red">
                                  {
                                    filterOutOutstandingDebit(
                                      ho_dr_final_with_prev,
                                      el,
                                    ).length
                                  }
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell></TableCell>
                        )}

                        <TableCell style={{ cursor: "pointer" }}>
                          <a
                            href={`#myCredit${el.debit_amt}`}
                            onClick={(e) => {
                              e.preventDefault();
                              showMatchingRow(
                                "headOfficeOutCr",
                                "credit_amt",
                                el.debit_amt,
                              );
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            {existsInBothArrays(
                              headOfficehOutCr,
                              headOfficehOutDr,
                              el.debit_amt,
                            )}
                          </a>
                        </TableCell>
                      </TableRow>

                      {el === expandingData && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>DEBITS</strong> The following debits
                              reconciled with the credits below{" "}
                              <ArrowDownwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {filterOutOutstandingDebit(
                            ho_dr_final_with_prev,
                            el,
                          ).map((sub, subIndex) => (
                            <TableRow
                              key={`ho-dr-sub-${subIndex}`}
                              className="element"
                              style={{ backgroundColor: "#fdf2f8" }}
                            >
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {moment(sub.value_date).format("DD-MM-YYYY")}
                              </TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {sub.description}
                              </TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {currencyFormatter.format(sub.debit_amt)}
                              </TableCell>
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                <Tooltip title="swap with main outstanding">
                                  <IconButton
                                    onClick={() =>
                                      swapHeadOfficeOutStandingDebit(el, sub)
                                    }
                                    size="small"
                                  >
                                    <SwapVertIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}

                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "14px" }}
                            >
                              <strong>CREDITS</strong> | The following credits
                              reconciled with the debits above{" "}
                              <ArrowUpwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {b_cr_final_with_prev
                            .filter(
                              (sub) =>
                                Number(sub.credit_amt) === Number(el.debit_amt),
                            )
                            .map((sub, subIndex) => (
                              <TableRow
                                key={`b-cr-match-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#e7e5e4" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.credit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                              </TableRow>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {renderPagerRow("headOfficeOutDr")}

                <TableRow>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    Total
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {calculateTotalValueHeadOfficeOutDr() > 0 &&
                      currencyFormatter.format(
                        calculateTotalValueHeadOfficeOutDr(),
                      )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      currencyFormatter.format(ourOutStandingsDebitsTotal)
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                </TableRow>

                {/* THEIR OUTSTANDING DEBIT */}
                <TableRow style={{ backgroundColor: "#ccc" }}>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      checked ? (
                        "LESS:THEIR OUTSTANDING DEBIT"
                      ) : (
                        "ADD: THEIR OUTSTANDING DEBIT"
                      )
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>
                    <a href="#theirCredits">
                      <Button
                        variant="outlined"
                        style={{ fontSize: "10px" }}
                        size="small"
                      >
                        THEIR CREDITS
                      </Button>
                    </a>
                    <a href="#myDebits">
                      <Button
                        variant="outlined"
                        style={{ fontSize: "10px", color: "red" }}
                        size="small"
                      >
                        OUR DEBITS
                      </Button>
                    </a>
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>

                {pageOf("branchOutDr").map((el, i) => {
                  const index = pageStart("branchOutDr") + i;
                  const rowKey = `branchOutDr-${index}`;

                  return (
                    <React.Fragment key={el.id || rowKey}>
                      <TableRow
                        ref={setDifferenceRowRef("branchOutDr", index)}
                        id="theirDebits"
                        style={getDifferenceHighlightStyle(rowKey, el)}
                        className={
                          branchOutDebitRows.includes(index) ? "selected" : ""
                        }
                      >
                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            <Checkbox
                              size="small"
                              checked={branchOutDebitRows.includes(index)}
                              onChange={() =>
                                handleRowCheckboxChangeBranchOutDr(index)
                              }
                            />
                          ) : (
                            <Skeleton
                              variant="circular"
                              width={20}
                              height={20}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            moment(el.value_date).format("DD-MM-YYYY")
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            el.description
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell
                          id={`theirDebit${el.debit_amt}`}
                          style={{ fontSize: "12px" }}
                        >
                          {selected ? (
                            currencyFormatter.format(el.debit_amt)
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        <TableCell style={{ fontSize: "12px" }}>
                          {selected ? (
                            data.date ? (
                              ageing(el.value_date, data.date)
                            ) : (
                              ""
                            )
                          ) : (
                            <Skeleton
                              variant="text"
                              sx={{ fontSize: "1rem" }}
                            />
                          )}
                        </TableCell>

                        {filterOutOutstandingDebit(b_dr_final_with_prev, el)
                          .length > 0 ? (
                          <TableCell style={{ fontSize: "12px" }}>
                            <IconButton
                              size="small"
                              onClick={() => handleExpandingData(el)}
                            >
                              {el === expandingData ? (
                                <ArrowDropUpIcon />
                              ) : (
                                <ArrowDropDownIcon />
                              )}
                            </IconButton>

                            <Tooltip title="reconciled items">
                              <IconButton size="small">
                                <Typography fontSize="12px" color="red">
                                  {
                                    filterOutOutstandingDebit(
                                      b_dr_final_with_prev,
                                      el,
                                    ).length
                                  }
                                </Typography>
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        ) : (
                          <TableCell></TableCell>
                        )}

                        <TableCell style={{ cursor: "pointer" }}>
                          <a
                            href={`#theirCredit${el.debit_amt}`}
                            onClick={(e) => {
                              e.preventDefault();
                              showMatchingRow(
                                "branchOutCr",
                                "credit_amt",
                                el.debit_amt,
                              );
                            }}
                            style={{ textDecoration: "none" }}
                          >
                            {existsInBothArrays(
                              branchOutCr,
                              branchOutDr,
                              el.debit_amt,
                            )}
                          </a>
                        </TableCell>
                      </TableRow>

                      {el === expandingData && (
                        <>
                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "12px" }}
                            >
                              <strong>DEBITS</strong> | The following debits
                              reconciled with the credits below{" "}
                              <ArrowDownwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {filterOutOutstandingDebit(
                            b_dr_final_with_prev,
                            el,
                          ).map((sub, subIndex) => (
                            <TableRow
                              key={`b-dr-sub-${subIndex}`}
                              className="element"
                              style={{ backgroundColor: "#fdf2f8" }}
                            >
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {moment(sub.value_date).format("DD-MM-YYYY")}
                              </TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {sub.description}
                              </TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                {currencyFormatter.format(sub.debit_amt)}
                              </TableCell>
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell
                                style={{ fontSize: "12px" }}
                              ></TableCell>
                              <TableCell style={{ fontSize: "12px" }}>
                                <Tooltip title="swap with main outstanding">
                                  <IconButton
                                    onClick={() =>
                                      swapBranchOutStandingDebit(el, sub)
                                    }
                                    size="small"
                                  >
                                    <SwapVertIcon />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          ))}

                          <TableRow>
                            <TableCell
                              colSpan={7}
                              className="element"
                              style={{ fontWeight: "bold", fontSize: "12px" }}
                            >
                              <strong>CREDITS</strong> | The following credits
                              reconciled with the debits above{" "}
                              <ArrowUpwardIcon style={{ fontSize: "12px" }} />
                            </TableCell>
                          </TableRow>

                          {ho_cr_final_with_prev
                            .filter(
                              (sub) =>
                                Number(sub.credit_amt) === Number(el.debit_amt),
                            )
                            .map((sub, subIndex) => (
                              <TableRow
                                key={`ho-cr-match-${subIndex}`}
                                className="element"
                                style={{ backgroundColor: "#e7e5e4" }}
                              >
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {moment(sub.value_date).format("DD-MM-YYYY")}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {sub.description}
                                </TableCell>
                                <TableCell style={{ fontSize: "12px" }}>
                                  {currencyFormatter.format(sub.credit_amt)}
                                </TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                                <TableCell
                                  style={{ fontSize: "12px" }}
                                ></TableCell>
                              </TableRow>
                            ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}

                {renderPagerRow("branchOutDr")}

                <TableRow>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    Total
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bolder" }}>
                    {calculateTotalValueBranchOutDr() > 0 &&
                      currencyFormatter.format(
                        calculateTotalValueBranchOutDr(),
                      )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                  <TableCell style={{ fontSize: "12px", fontWeight: "bold" }}>
                    {selected ? (
                      currencyFormatter.format(theirOutStandingsDebitsTotal)
                    ) : (
                      <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                    )}
                  </TableCell>
                  <TableCell style={{ fontSize: "12px" }}>---------</TableCell>
                  <TableCell style={{ fontSize: "12px" }}></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            minHeight: "50vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            p: 3,
            borderRadius: "18px",
            border: "1px dashed #cbd5df",
            background: colors.surface,
          }}
        >
          <Box sx={{ maxWidth: 520 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
              {headOfficeData[0] && branchData[0]
                ? "Both statements are loaded"
                : "Get started"}
            </Typography>
            <Typography sx={{ fontSize: 14, color: colors.textSecondary, mb: 2 }}>
              {headOfficeData[0] && branchData[0]
                ? "Click Reconcile to match them. Outstanding items from the last reconciliation are brought in automatically."
                : "Follow these steps to reconcile two locations."}
            </Typography>
            <Stack spacing={1.2}>
              <StepLine done={Boolean(primary && secondary && data.date)} n={1}>
                Choose the primary and secondary locations and the &quot;as
                at&quot; date.
              </StepLine>
              <StepLine done={Boolean(headOfficeData[0])} n={2}>
                Import the primary location&apos;s statement.
              </StepLine>
              <StepLine done={Boolean(branchData[0])} n={3}>
                Import the secondary location&apos;s statement.
              </StepLine>
              <StepLine done={false} n={4}>
                Click <strong>Reconcile</strong>, choose Debit or Credit
                account, enter both balances, then <strong>Submit</strong>{" "}
                for approval.
              </StepLine>
            </Stack>
          </Box>
        </Paper>
      )}

      {checkedSummary.totalCheckedRows > 0 && (
        <Paper
          elevation={10}
          sx={{
            position: "fixed",
            right: 16,
            bottom: 90,
            zIndex: 1250,
            width: 240,
            borderRadius: "14px",
            overflow: "hidden",
            border: "1px solid #d7e2ec",
            background: "#ffffff",
            boxShadow: "0 14px 35px rgba(15, 23, 42, 0.22)",
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.2,
              background: "linear-gradient(135deg, #1F354D 0%, #33597c 100%)",
              color: "#fff",
            }}
          >
            <Typography sx={{ fontWeight: 800, fontSize: "13px" }}>
              Checked Transactions
            </Typography>

            <Typography sx={{ fontSize: "10px", opacity: 0.85 }}>
              {checkedSummary.totalCheckedRows} selected row
              {checkedSummary.totalCheckedRows > 1 ? "s" : ""}
            </Typography>
          </Box>

          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                Checked Credit
              </Typography>

              <Typography
                sx={{ fontSize: "12px", fontWeight: 800, color: "#15803d" }}
              >
                {currencyFormatter.format(checkedSummary.totalCheckedCredit)}
              </Typography>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Typography sx={{ fontSize: "11px", color: "#64748b" }}>
                Checked Debit
              </Typography>

              <Typography
                sx={{ fontSize: "12px", fontWeight: 800, color: "#b91c1c" }}
              >
                {currencyFormatter.format(checkedSummary.totalCheckedDebit)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography sx={{ fontSize: "11px", fontWeight: 700 }}>
                Difference
              </Typography>

              <Typography
                sx={{
                  fontSize: "12px",
                  fontWeight: 900,
                  color:
                    checkedSummary.difference === 0
                      ? "#15803d"
                      : checkedSummary.difference > 0
                        ? "#1d4ed8"
                        : "#b91c1c",
                }}
              >
                {currencyFormatter.format(checkedSummary.difference)}
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </div>
  );
}
